import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertTriangle, Loader2, ChevronDown, ChevronRight, History } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("pr_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, {
  day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
}) : "—");
const dur = (a, b) => (a && b ? `${Math.round((new Date(b) - new Date(a)) / 1000)}s` : "—");

const STATUS = {
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  partial: "bg-amber-50 text-amber-800 border-amber-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  skipped: "bg-slate-100 text-slate-600 border-slate-200",
  running: "bg-cyan-50 text-cyan-800 border-cyan-200",
  interrupted: "bg-orange-50 text-orange-800 border-orange-200",
};

function RunRow({ run }) {
  const [open, setOpen] = useState(["failed", "partial", "interrupted"].includes(run.status));
  return (
    <>
      <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(!open)}
          data-testid={`sync-run-${run.id}`}>
        <td className="px-3 py-2">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
        <td className="px-3 py-2 whitespace-nowrap">{fmt(run.started_at)}</td>
        <td className="px-3 py-2 font-mono text-xs">{run.what}</td>
        <td className="px-3 py-2 text-xs text-slate-600">{run.trigger}</td>
        <td className="px-3 py-2">
          <span className={`text-[11px] font-semibold uppercase px-2 py-0.5 border rounded-sm ${STATUS[run.status] || STATUS.skipped}`}
                data-testid={`sync-run-status-${run.id}`}>
            {run.status === "running" && <Loader2 size={11} className="inline animate-spin mr-1" />}{run.status}
          </span>
        </td>
        <td className="px-3 py-2 text-xs font-mono">{dur(run.started_at, run.finished_at)}</td>
      </tr>
      {open && (
        <tr className="bg-slate-50/60">
          <td />
          <td colSpan={5} className="px-3 py-2">
            {run.error && <div className="text-xs text-slate-600 mb-1">{run.error}</div>}
            <ul className="space-y-1">
              {(run.steps || []).map((s) => (
                <li key={s.name} className="text-xs flex flex-wrap items-start gap-x-2" data-testid={`sync-step-${run.id}-${s.name}`}>
                  {s.ok ? <CheckCircle2 size={13} className="text-emerald-600 mt-0.5" />
                        : <AlertTriangle size={13} className="text-rose-600 mt-0.5" />}
                  <span className="font-mono w-32 sm:w-40 flex-shrink-0">{s.name}</span>
                  <span className="text-slate-500 w-24">{s.rows ?? "—"} rows</span>
                  <span className="text-slate-500 w-20">{Math.round((s.ms || 0) / 1000)}s</span>
                  <span className="text-slate-500 w-20">{s.attempts > 1 ? `${s.attempts} tries` : ""}</span>
                  {s.error && <span className="text-rose-700 break-all basis-full sm:basis-auto">{s.error}</span>}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}

export function SapSyncHistory() {
  const [runs, setRuns] = useState(null);
  useEffect(() => {
    const load = () => axios.get(`${API}/admin/sap/sync/history?limit=15`, { headers: authHeaders() })
      .then((r) => setRuns(r.data.runs)).catch(() => setRuns([]));
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="sap-sync-history">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        <History size={16} className="text-cyan-700" />
        <h3 className="font-semibold text-slate-800 text-sm">Sync history</h3>
        <span className="text-xs text-slate-500">Full sync runs automatically at 02:00 and 13:10 IST · failed steps retry 3×</span>
      </div>
      {runs === null ? (
        <div className="p-4 text-sm text-slate-500"><Loader2 size={14} className="inline animate-spin mr-1" />Loading…</div>
      ) : runs.length === 0 ? (
        <div className="p-4 text-sm text-slate-500" data-testid="sap-sync-history-empty">No sync runs recorded yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wide text-slate-500 bg-slate-50">
              <tr><th /><th className="px-3 py-2 text-left">Started</th><th className="px-3 py-2 text-left">What</th>
                <th className="px-3 py-2 text-left">Trigger</th><th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Took</th></tr>
            </thead>
            <tbody>{runs.map((r) => <RunRow key={r.id} run={r} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
